import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp, query, onSnapshot, orderBy, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import '../Admin.css';

function Items() {
  const [itemName, setItemName] = useState('');
  const [selectedService, setSelectedService] = useState('');

  // Image Upload State (Base64)
  const [imageUrl, setImageUrl] = useState('');
  const [imagePreview, setImagePreview] = useState('');

  // 2 Pricing Categories (Order: Standard, Express)
  const [standardPrice, setStandardPrice] = useState('');
  const [expressPrice, setExpressPrice] = useState('');

  // KG Pricing (Now with Standard and Express per slab)
  const [useKgPricing, setUseKgPricing] = useState(false);
  const [priceSlabs, setPriceSlabs] = useState([
    { min: 0, max: 1, standardPrice: '', expressPrice: '' }
  ]);

  const [services, setServices] = useState([]);
  const [items, setItems] = useState([]);

  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);

  // Fetch Services & Items
  useEffect(() => {
    const qServices = query(collection(db, "services"), orderBy("createdAt", "asc"));
    const unsubServices = onSnapshot(qServices, (snapshot) => {
      setServices(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    const qItems = query(collection(db, "items"), orderBy("createdAt", "desc"));
    const unsubItems = onSnapshot(qItems, (snapshot) => {
      setItems(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    return () => { unsubServices(); unsubItems(); };
  }, []);

  // Convert File to Base64
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert("File size exceeds 2MB limit! Please upload a smaller image.");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageUrl(reader.result);
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedService) return alert("Please select a service!");

    setLoading(true);

    const formattedSlabs = priceSlabs.map(slab => ({
      min: Number(slab.min),
      max: Number(slab.max),
      standardPrice: Number(slab.standardPrice),
      expressPrice: Number(slab.expressPrice)
    }));

    const itemData = {
      name: itemName,
      serviceId: selectedService,
      serviceName: services.find(s => s.id === selectedService)?.name || '',
      imageUrl: imageUrl,
      pricingType: useKgPricing ? 'kg' : 'fixed',
      standardPrice: useKgPricing ? null : Number(standardPrice),
      expressPrice: useKgPricing ? null : Number(expressPrice),
      priceSlabs: useKgPricing ? formattedSlabs : [],
      updatedAt: serverTimestamp()
    };

    try {
      if (editId) {
        await updateDoc(doc(db, "items", editId), itemData);
        setEditId(null);
      } else {
        await addDoc(collection(db, "items"), { ...itemData, createdAt: serverTimestamp() });
      }
      resetForm();
    } catch (error) {
      console.error("Error saving item: ", error);
      alert("Error saving item. Please try again.");
    }

    setLoading(false);
  };

  const resetForm = () => {
    setItemName('');
    setStandardPrice('');
    setExpressPrice('');
    setSelectedService('');
    setImageUrl('');
    setImagePreview('');
    setEditId(null);
    setUseKgPricing(false);
    setPriceSlabs([
      { min: 0, max: 1, standardPrice: '', expressPrice: '' }
    ]);
  };

  const handleEdit = (item) => {
    setEditId(item.id);
    setItemName(item.name);
    setSelectedService(item.serviceId);
    setImageUrl(item.imageUrl || '');
    setImagePreview(item.imageUrl || '');

    setUseKgPricing(item.pricingType === 'kg');

    if (item.pricingType === 'kg') {
      setPriceSlabs(
        item.priceSlabs?.length > 0 
          ? item.priceSlabs 
          : [{ min: 0, max: 1, standardPrice: '', expressPrice: '' }]
      );
    } else {
      setStandardPrice(item.standardPrice ?? '');
      setExpressPrice(item.expressPrice ?? '');
    }

    window.scrollTo(0, 0);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this item?")) {
      await deleteDoc(doc(db, "items", id));
    }
  };

  return (
    <div className="add-services-container">

      {/* FORM */}
      <section className="form-section">
        <div className="form-card-full">
          <h3>{editId ? 'Edit Item' : 'Add New Item'}</h3>

          <form onSubmit={handleSubmit} className="horizontal-form">
            <div className="form-row">

              <div className="input-group">
                <label>Item Name</label>
                <input type="text" value={itemName} onChange={(e)=>setItemName(e.target.value)} required />
              </div>

              <div className="input-group">
                <label>Service</label>
                <select value={selectedService} onChange={(e)=>setSelectedService(e.target.value)} required>
                  <option value="">Select Service</option>
                  {services.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>

              {/* Image Upload Input */}
              <div className="input-group">
                <label>Item Image</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input 
                    type="file" 
                    accept="image/*" 
                    onChange={handleImageChange} 
                    required={!editId && !imageUrl}
                    style={{ fontSize: '0.85rem' }}
                  />
                  {imagePreview && (
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #ccc' }} 
                    />
                  )}
                </div>
              </div>

              {/* Pricing Type */}
              <div className="input-group">
                <label>Pricing Type</label>
                <select value={useKgPricing ? 'kg' : 'fixed'} onChange={(e)=>setUseKgPricing(e.target.value === 'kg')}>
                  <option value="fixed">Fixed</option>
                  <option value="kg">Per KG</option>
                </select>
              </div>

              {/* FIXED PRICE CATEGORIES (STANDARD -> EXPRESS) */}
              {!useKgPricing ? (
                <>
                  <div className="input-group">
                    <label>Standard (AED)</label>
                    <input 
                      type="number" 
                      step="any"
                      placeholder="Standard" 
                      value={standardPrice} 
                      onChange={(e)=>setStandardPrice(e.target.value)} 
                      required 
                    />
                  </div>

                  <div className="input-group">
                    <label>Express (AED)</label>
                    <input 
                      type="number" 
                      step="any"
                      placeholder="Express" 
                      value={expressPrice} 
                      onChange={(e)=>setExpressPrice(e.target.value)} 
                      required 
                    />
                  </div>
                </>
              ) : (
                /* KG PRICING WITH 2 CATEGORIES (STD -> EXPRESS) */
                <div className="kg-pricing-box" style={{ width: '100%', marginTop: '10px' }}>
                  <label style={{ fontWeight: 'bold', marginBottom: '8px', display: 'block' }}>
                    KG Pricing Slabs (AED)
                  </label>

                  {priceSlabs.map((slab, index) => (
                    <div key={index} className="kg-row" style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <input
                        type="number"
                        placeholder="From KG"
                        value={slab.min}
                        style={{ width: '80px' }}
                        onChange={(e)=>{
                          const updated = [...priceSlabs];
                          updated[index].min = e.target.value;
                          setPriceSlabs(updated);
                        }}
                        required
                      />

                      <input
                        type="number"
                        placeholder="To KG"
                        value={slab.max}
                        style={{ width: '80px' }}
                        onChange={(e)=>{
                          const updated = [...priceSlabs];
                          updated[index].max = e.target.value;
                          setPriceSlabs(updated);
                        }}
                        required
                      />

                      <input
                        type="number"
                        step="any"
                        placeholder="Std AED"
                        value={slab.standardPrice}
                        style={{ width: '100px' }}
                        onChange={(e)=>{
                          const updated = [...priceSlabs];
                          updated[index].standardPrice = e.target.value;
                          setPriceSlabs(updated);
                        }}
                        required
                      />

                      <input
                        type="number"
                        step="any"
                        placeholder="Express AED"
                        value={slab.expressPrice}
                        style={{ width: '100px' }}
                        onChange={(e)=>{
                          const updated = [...priceSlabs];
                          updated[index].expressPrice = e.target.value;
                          setPriceSlabs(updated);
                        }}
                        required
                      />

                      <button 
                        type="button" 
                        className="delete-action-btn"
                        onClick={()=>{
                          const updated = priceSlabs.filter((_,i)=>i!==index);
                          setPriceSlabs(updated);
                        }}>
                        X
                      </button>
                    </div>
                  ))}

                  <button 
                    type="button" 
                    className="cancel-btn"
                    style={{ marginTop: '5px' }}
                    onClick={()=>{
                      setPriceSlabs([...priceSlabs, { min:'', max:'', standardPrice:'', expressPrice:'' }]);
                    }}>
                    + Add KG Slab
                  </button>
                </div>
              )}

              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? (editId ? 'Updating...' : 'Adding...') : (editId ? 'Update Item' : 'Add Item')}
              </button>

              {editId && <button type="button" onClick={resetForm} className="cancel-btn">Cancel</button>}

            </div>
          </form>
        </div>
      </section>

      {/* TABLE */}
      <section className="table-section">
        <div className="table-card">
          <table className="modern-admin-table">
            <thead>
              <tr>
                <th>Sl</th>
                <th>Image</th>
                <th>Item</th>
                <th>Service</th>
                <th>Pricing Details</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {items.map((item, index) => (
                <tr key={item.id}>
                  <td>{index+1}</td>
                  <td>
                    {item.imageUrl ? (
                      <img 
                        src={item.imageUrl} 
                        alt={item.name} 
                        style={{ width: '45px', height: '45px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #eee' }} 
                      />
                    ) : (
                      <div className="table-icon-circle"><i className="fas fa-tshirt"></i></div>
                    )}
                  </td>
                  <td><strong>{item.name}</strong></td>
                  <td><span className="service-tag">{item.serviceName}</span></td>

                  <td>
                    {item.pricingType === 'kg' ? (
                      <div style={{ fontSize: '0.85rem', lineHeight: '1.4' }}>
                        {item.priceSlabs?.map((s,i)=>(
                          <div key={i} style={{ marginBottom: '4px' }}>
                            <strong>{s.min}-{s.max} kg:</strong> Std AED {s.standardPrice} | Express AED {s.expressPrice}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ fontSize: '0.85rem', lineHeight: '1.4' }}>
                        <div><strong>Std:</strong> AED {item.standardPrice}</div>
                        <div><strong>Express:</strong> AED {item.expressPrice}</div>
                      </div>
                    )}
                  </td>

                  <td>
                    <button onClick={()=>handleEdit(item)} className="edit-btn">
                      <i className="fas fa-edit"></i>
                    </button>

                    <button onClick={()=>handleDelete(item.id)} className="delete-action-btn">
                      <i className="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>

          </table>
        </div>
      </section>

    </div>
  );
}

export default Items;